import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { WEB_MAX_CONTENT_WIDTH } from '../../theme/webLayoutWidths';
import {
  ActivityIndicator,
  Platform,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import Ionicons from '@react-native-vector-icons/ionicons/static';
import { KeyboardStickyView } from 'react-native-keyboard-controller';
import { TextInput, Button, HelperText } from 'react-native-paper';
import { Picker } from '@react-native-picker/picker';
import PhoneInput, {
  ICountry,
  IPhoneInputRef,
} from 'rn-international-phone-number';
import {
  PREFERRED_COUNTRIES,
  buildE164FromInput,
  derivePhoneInputFromValue,
} from '../../utils/phone';
import { useDetectedCountry } from '../../hooks/useDetectedCountry';
import { useNavigation, useRoute } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { GTFonts, useTheme } from '../../theme';
import {
  borderWidth,
  fs,
  iconSize,
  normalizeEmail,
  s,
  v,
} from '../../utils/libs';
import KeyboardAwareFormScroll from '../../components/Reusables/KeyboardAwareFormScroll';
import useBackofficeAxios from '../../hooks/useBackofficeAxios';
import useBackofficeOwnerAuth from '../../hooks/useBackofficeOwnerAuth';
import { usePickerItemColor } from '../../hooks/usePickerItemColor';
import {
  addSupplierInformation,
  getSupplierInformation,
  parseSupplierResponse,
  updateSupplierInformation,
} from '../../api/backofficeSuppliers';
import { AppSnackbar } from '../../services/AppSnackbar';
import { useAppDispatch, useAppSelector } from '../../redux/helper';
import {
  setSupplierByIdData,
  setSupplierByIdError,
  setSupplierByIdLoading,
  upsertSupplierInList,
} from '../../redux/backoffice/suppliersSlice';
import type { RootStackParamList } from '../../navigation/AppStack';
import { getApiMessage } from '../../utils/apiError';

type RouteParams = { supplierId?: string };

type SupplierForm = {
  name: string;
  contactPerson: string;
  phone: string;
  whatsapp: string;
  email: string;
  physicalAddress: string;
  taxVat: string;
  status: string;
  preferredCommunication: string;
  notes: string;
};

const initialForm: SupplierForm = {
  name: '',
  contactPerson: '',
  phone: '',
  whatsapp: '',
  email: '',
  physicalAddress: '',
  taxVat: '',
  status: 'active',
  preferredCommunication: 'any',
  notes: '',
};

const BackOfficeAddSupplierScreen: React.FC = () => {
  const { theme } = useTheme();
  const { pickerItemColor, pickerItemColorSecondary } = usePickerItemColor();
  const styles = useMemo(() => createStyles(theme), [theme]);
  const route = useRoute();
  const { supplierId } = (route.params || {}) as RouteParams;
  const isEdit = Boolean(supplierId);

  const inputOutlineProps = useMemo(
    () => ({
      outlineColor: theme.colors.borderGray,
      activeOutlineColor: theme.colors.primary,
    }),
    [theme],
  );

  const webPlaceholderTheme = useMemo(() => {
    if (Platform.OS === 'web') {
      const hexToRgba = (hex: string, alpha: number) => {
        const r = parseInt(hex.slice(1, 3), 16);
        const g = parseInt(hex.slice(3, 5), 16);
        const b = parseInt(hex.slice(5, 7), 16);
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
      };
      const placeholderColor = theme.colors.textSecondary
        ? hexToRgba(theme.colors.textSecondary, 0.55)
        : 'rgba(128, 128, 128, 0.55)';
      return {
        theme: {
          fonts: {
            regular: { fontFamily: GTFonts.regular, fontWeight: '400' as any },
          },
          colors: {
            placeholder: placeholderColor,
            onSurfaceVariant: placeholderColor,
          },
        },
      };
    }
    return {};
  }, [theme.colors.textSecondary]);

  const navigation =
    useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const axiosBackoffice = useBackofficeAxios();
  const { owner } = useBackofficeOwnerAuth();
  const dispatch = useAppDispatch();

  const supplierEntity = useAppSelector(
    state => state.backofficeSuppliers.supplierById[String(supplierId || '')],
  );
  const supplierFromList = useAppSelector(state =>
    supplierId
      ? state.backofficeSuppliers.list.items.find(s => s._id === supplierId)
      : null,
  );
  const cachedSupplier = supplierEntity?.data || supplierFromList;
  const supplierLoading = supplierEntity?.loading ?? false;
  const supplierError = supplierEntity?.error;

  const [form, setForm] = useState<SupplierForm>(initialForm);
  const [saving, setSaving] = useState(false);
  const [hydrated, setHydrated] = useState(false);
  const [isAddressExpanded, setIsAddressExpanded] = useState(false);

  const { countryCode: detectedCountryCode, iCountry } = useDetectedCountry();

  const [phoneInputValue, setPhoneInputValue] = useState('');
  const [phoneSelectedCountry, setPhoneSelectedCountry] = useState<
    ICountry | undefined
  >(undefined);
  const phoneInputRef = React.useRef<IPhoneInputRef | null>(null);
  const [validPhone, setValidPhone] = useState(false);
  const [phoneFocused, setPhoneFocused] = useState(false);

  const [whatsappInputValue, setWhatsappInputValue] = useState('');
  const [whatsappSelectedCountry, setWhatsappSelectedCountry] = useState<
    ICountry | undefined
  >(undefined);
  const whatsappInputRef = React.useRef<IPhoneInputRef | null>(null);
  const [validWhatsapp, setValidWhatsapp] = useState(false);
  const [whatsappFocused, setWhatsappFocused] = useState(false);

  const hasInitializedPhoneFromHydrationRef = React.useRef(false);
  const lastInitializedSupplierIdRef = React.useRef<string | null>(null);

  useEffect(() => {
    if (!isEdit || !supplierId) {
      lastInitializedSupplierIdRef.current = null;
      hasInitializedPhoneFromHydrationRef.current = false;
    }
  }, [isEdit, supplierId]);

  const setField = (key: keyof SupplierForm, value: string) => {
    setForm(prev => ({ ...prev, [key]: value }));
  };

  const hydrateForm = useCallback((entity: any) => {
    if (!entity) return;
    setForm({
      name: String(entity.bp_supplier_name || ''),
      contactPerson: String(entity.bp_supplier_contact_person_name || ''),
      phone: String(entity.bp_supplier_phone_number || ''),
      whatsapp: String(entity.bp_supplier_whatsapp_number || ''),
      email: String(entity.bp_supplier_email_address || ''),
      physicalAddress: String(entity.bp_supplier_physical_address || ''),
      taxVat: String(entity.bp_supplier_tax_vat_number || ''),
      status: String(entity.bp_supplier_status || 'active'),
      preferredCommunication: (() => {
        const raw = String(
          entity.bp_supplier_preferred_communication_channel || 'any',
        );
        return raw === 'phone' ? 'sms' : raw;
      })(),
      notes: String(entity.bp_supplier_notes || ''),
    });
    setIsAddressExpanded(
      Boolean(
        entity.bp_supplier_physical_address || entity.bp_supplier_tax_vat_number,
      ),
    );
    setHydrated(true);
  }, []);

  const fetchSupplier = useCallback(async () => {
    if (!supplierId || !owner?.accessToken) return;
    dispatch(setSupplierByIdLoading(supplierId));
    try {
      const res = await getSupplierInformation(
        axiosBackoffice as any,
        supplierId,
        { accessToken: owner.accessToken },
      );
      const parsed = parseSupplierResponse(res);
      if (!parsed?.success || !parsed.supplier) {
        dispatch(
          setSupplierByIdError({
            id: supplierId,
            error: parsed?.message || 'Failed to load supplier',
          }),
        );
        return;
      }
      dispatch(setSupplierByIdData({ id: supplierId, data: parsed.supplier }));
      dispatch(upsertSupplierInList(parsed.supplier));
      hydrateForm(parsed.supplier);
    } catch (error: any) {
      dispatch(
        setSupplierByIdError({
          id: supplierId,
          error: getApiMessage(error, 'Failed to load supplier'),
        }),
      );
    }
  }, [axiosBackoffice, dispatch, hydrateForm, owner?.accessToken, supplierId]);

  useEffect(() => {
    if (!isEdit || !supplierId) return;
    if (cachedSupplier && !hydrated) {
      hydrateForm(cachedSupplier);
      return;
    }
    if (!cachedSupplier && !supplierLoading && !supplierError) {
      fetchSupplier();
    }
  }, [
    isEdit,
    supplierId,
    cachedSupplier,
    hydrated,
    supplierLoading,
    supplierError,
    fetchSupplier,
    hydrateForm,
  ]);

  useEffect(() => {
    if (!phoneSelectedCountry && iCountry) {
      setPhoneSelectedCountry(iCountry);
    }
  }, [phoneSelectedCountry, iCountry]);

  useEffect(() => {
    if (!whatsappSelectedCountry && iCountry) {
      setWhatsappSelectedCountry(iCountry);
    }
  }, [whatsappSelectedCountry, iCountry]);

  // Ref validity updates after commit (rn-international-phone-number); sync like LoginScreen.
  useEffect(() => {
    setValidPhone(!!phoneInputRef.current?.isValidPhoneNumber);
  }, [form.phone]);

  useEffect(() => {
    setValidWhatsapp(!!whatsappInputRef.current?.isValidPhoneNumber);
  }, [form.whatsapp]);

  useEffect(() => {
    if (!hydrated || !cachedSupplier || !supplierId) return;
    if (lastInitializedSupplierIdRef.current === supplierId) return;
    lastInitializedSupplierIdRef.current = supplierId;
    hasInitializedPhoneFromHydrationRef.current = true;

    const phoneRaw = String(cachedSupplier.bp_supplier_phone_number || '');
    if (phoneRaw) {
      const derived = derivePhoneInputFromValue(phoneRaw);
      setPhoneInputValue(derived.nationalNumber);
      if (derived.country) {
        setPhoneSelectedCountry(derived.country);
      }
    }

    const whatsappRaw = String(
      cachedSupplier.bp_supplier_whatsapp_number || '',
    );
    if (whatsappRaw) {
      const derived = derivePhoneInputFromValue(whatsappRaw);
      setWhatsappInputValue(derived.nationalNumber);
      if (derived.country) {
        setWhatsappSelectedCountry(derived.country);
      }
    }
  }, [
    supplierId,
    hydrated,
    cachedSupplier?.bp_supplier_phone_number,
    cachedSupplier?.bp_supplier_whatsapp_number,
  ]);

  const EMAIL_REGEX = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
  const isEmailValid = useMemo(() => {
    if (!form.email.trim()) return true;
    return EMAIL_REGEX.test(form.email.trim());
  }, [form.email]);

  const isNameValid = useMemo(() => {
    const trimmed = form.name.trim();
    return trimmed.length === 0 || trimmed.length >= 3;
  }, [form.name]);

  const canSubmit = useMemo(() => {
    const trimmed = form.name.trim();
    return trimmed.length >= 3 && isEmailValid && !saving;
  }, [form.name, isEmailValid, saving]);

  const handleSubmit = async () => {
    if (!canSubmit || !owner?.accessToken) return;

    const payload = {
      bp_supplier_name: form.name.trim(),
      bp_supplier_contact_person_name: form.contactPerson.trim() || undefined,
      bp_supplier_phone_number: form.phone.trim() || undefined,
      bp_supplier_whatsapp_number:
        form.preferredCommunication === 'whatsapp' ||
        form.preferredCommunication === 'any'
          ? form.whatsapp.trim() || ''
          : '',
      bp_supplier_email_address: normalizeEmail(form.email) || undefined,
      bp_supplier_physical_address: form.physicalAddress.trim() || undefined,
      bp_supplier_tax_vat_number: form.taxVat.trim() || undefined,
      bp_supplier_preferred_communication_channel:
        form.preferredCommunication.trim() || 'any',
      bp_supplier_notes: isEdit ? form.notes.trim() || undefined : '',
      bp_supplier_status: form.status.trim() || 'active',
    };

    setSaving(true);
    try {
      if (isEdit && supplierId) {
        const res = await updateSupplierInformation(
          axiosBackoffice as any,
          supplierId,
          payload,
          { accessToken: owner.accessToken },
        );
        const ok = res?.data?.success === true || res?.data?.status === 200;
        if (!ok) {
          AppSnackbar.show(
            String(res?.data?.message || 'Failed to update supplier'),
            'error',
          );
          return;
        }
        const parsed = parseSupplierResponse(res);
        const updated =
          parsed?.supplier || res?.data?.supplier || res?.data?.data;
        if (updated?._id) {
          dispatch(setSupplierByIdData({ id: supplierId, data: updated }));
          dispatch(upsertSupplierInList(updated));
        }
        AppSnackbar.show(
          String(res?.data?.message || 'Supplier updated successfully'),
          'success',
        );
        navigation.replace('BackOfficeSupplierDetails', {
          supplierId: String(supplierId),
        });
      } else {
        const res = await addSupplierInformation(
          axiosBackoffice as any,
          payload,
          { accessToken: owner.accessToken },
        );
        const ok = res?.data?.success === true || res?.data?.status === 200;
        if (!ok) {
          AppSnackbar.show(
            String(res?.data?.message || 'Failed to create supplier'),
            'error',
          );
          return;
        }
        const parsed = parseSupplierResponse(res);
        const supplier =
          parsed?.supplier || res?.data?.supplier || res?.data?.data;
        if (supplier?._id) {
          dispatch(upsertSupplierInList(supplier));
        }
        AppSnackbar.show(
          String(res?.data?.message || 'Supplier created successfully'),
          'success',
        );
        try {
          navigation.popTo('BackOffice', { screen: 'Suppliers' });
        } catch {
          navigation.navigate('BackOffice', { screen: 'Suppliers' });
        }
      }
    } catch (error: any) {
      AppSnackbar.show(
        getApiMessage(
          error,
          isEdit ? 'Failed to update supplier' : 'Failed to create supplier',
        ),
        'error',
      );
    } finally {
      setSaving(false);
    }
  };

  if (isEdit && !hydrated && (supplierLoading || !supplierError)) {
    return (
      <View style={[styles.container, styles.loaderWrap]}>
        <ActivityIndicator size="large" color={theme.colors.primary} />
        <Text style={styles.loaderText}>Loading supplier...</Text>
      </View>
    );
  }

  if (isEdit && supplierError && !hydrated) {
    return (
      <View style={[styles.container, styles.loaderWrap]}>
        <Text style={styles.errorText}>{supplierError}</Text>
        <Button
          mode="outlined"
          onPress={() => navigation.goBack()}
          style={{ marginTop: v(12) }}>
          Go Back
        </Button>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <KeyboardAwareFormScroll
        style={styles.sheetContent}
        contentContainerStyle={styles.sheetContentContainer}
        keyboardShouldPersistTaps="handled"
        // baseBottomPadding={v(20)}
        bottomOffset={v(240)}>
        <Text style={styles.subtitle}>
          {isEdit
            ? 'Update the supplier contact and business details.'
            : 'Add a supplier so you can order your items directly.'}
        </Text>

        <Text style={styles.sectionTitle}>Supplier Details</Text>
        <TextInput
          mode="outlined"
          label="Supplier Name *"
          value={form.name}
          onChangeText={text => setField('name', text)}
          placeholderTextColor={theme.colors.textSecondary}
          {...inputOutlineProps}
          {...webPlaceholderTheme}
          style={styles.input}
        />
        {form.name.trim() && !isNameValid ? (
          <HelperText type="error" visible>
            Name must be 3 or more letters
          </HelperText>
        ) : null}
        <TextInput
          mode="outlined"
          label="Contact Person Name"
          value={form.contactPerson}
          onChangeText={text => setField('contactPerson', text)}
          placeholderTextColor={theme.colors.textSecondary}
          {...inputOutlineProps}
          {...webPlaceholderTheme}
          style={styles.input}
        />
        <Text style={styles.fieldLabel}>Phone</Text>
        <View style={[styles.phoneInputOutline]}>
          <PhoneInput
            ref={phoneInputRef}
            defaultCountry={detectedCountryCode as any}
            value={phoneInputValue}
            modalType="bottomSheet"
            placeholder="720 222 222"
            minBottomsheetHeight={'50%'}
            initialBottomsheetHeight={'80%'}
            maxBottomsheetHeight={'92%'}
            popularCountries={PREFERRED_COUNTRIES}
            modalPopularCountriesTitle="Popular countries"
            modalAllCountriesTitle="All countries"
            onFocus={() => setPhoneFocused(true)}
            onBlur={() => setPhoneFocused(false)}
            onChangePhoneNumber={v => {
              const startsWithZero = v.startsWith('0');
              if (startsWithZero) {
                v = v.slice(1);
              }
              setPhoneInputValue(v);
              const full = buildE164FromInput(
                v,
                phoneSelectedCountry || phoneInputRef.current?.getCountry?.(),
              );
              setField('phone', full);
            }}
            country={phoneSelectedCountry}
            onChangeCountry={c => {
              setPhoneSelectedCountry(c);
              const full = buildE164FromInput(phoneInputValue, c);
              setField('phone', full);
            }}
            phoneInputStyles={{
              container: {
                backgroundColor: 'transparent',
                height: '100%',
                alignItems: 'center',
              },
              flagContainer: {
                paddingHorizontal: s(8),
              },
              divider: {
                width: borderWidth(Platform.OS === 'web' ? 0.5 : 1),
              },
              callingCode: {
                fontFamily: GTFonts.regular,
                fontSize: fs(16),
                lineHeight: fs(18),
                color: theme.colors.text,
              },
              input: {
                fontFamily: GTFonts.regular,
                fontSize: fs(16),
                lineHeight: fs(18),
                color: theme.colors.text,
                ...(Platform.OS === 'web' && { outlineStyle: 'none' }),
              } as any,
            }}
          />
        </View>
        {phoneFocused && form.phone && !validPhone ? (
          <HelperText type="error" visible>
            Invalid phone number
          </HelperText>
        ) : null}
        <TextInput
          mode="outlined"
          label="Email Address"
          value={form.email}
          onChangeText={text => setField('email', text?.toLowerCase())}
          autoCapitalize="none"
          keyboardType="email-address"
          placeholderTextColor={theme.colors.textSecondary}
          {...inputOutlineProps}
          {...webPlaceholderTheme}
          style={styles.input}
        />
        {form.email.trim() ? (
          <HelperText type={isEmailValid ? 'info' : 'error'} visible>
            {isEmailValid ? null : 'Enter a valid email'}
          </HelperText>
        ) : null}

        {isEdit ? (
          <>
            <Text style={styles.fieldLabel}>Status</Text>
            <View style={styles.pickerContainer}>
              <Picker
                selectedValue={form.status}
                onValueChange={value => setField('status', String(value))}
                dropdownIconColor={theme.colors.primary}
                mode="dropdown"
                style={styles.picker}>
                <Picker.Item
                  label="Active"
                  value="active"
                  color={pickerItemColor}
                />
                <Picker.Item
                  label="Inactive"
                  value="inactive"
                  color={pickerItemColor}
                />
              </Picker>
            </View>
          </>
        ) : null}

        <Text style={styles.sectionTitle}>Preferred Communication</Text>
        <View style={styles.pickerContainer}>
          <Picker
            selectedValue={form.preferredCommunication}
            onValueChange={value => {
              const next = String(value);
              setField('preferredCommunication', next);
              if (next !== 'whatsapp' && next !== 'any') {
                setField('whatsapp', '');
                setWhatsappInputValue('');
                setWhatsappSelectedCountry(undefined);
                setWhatsappFocused(false);
              }
            }}
            dropdownIconColor={theme.colors.primary}
            mode="dropdown"
            style={styles.picker}>
            <Picker.Item
              label="Any"
              value="any"
              color={pickerItemColorSecondary}
            />
            <Picker.Item label="Email" value="email" color={pickerItemColor} />
            <Picker.Item
              label="WhatsApp"
              value="whatsapp"
              color={pickerItemColor}
            />
            <Picker.Item label="SMS" value="sms" color={pickerItemColor} />
          </Picker>
        </View>
        {form.preferredCommunication === 'whatsapp' ||
        form.preferredCommunication === 'any' ? (
          <>
            <Text style={styles.fieldLabel}>WhatsApp Number</Text>
            <View style={[styles.phoneInputOutline]}>
              <PhoneInput
                ref={whatsappInputRef}
                defaultCountry={detectedCountryCode as any}
                value={whatsappInputValue}
                modalType="bottomSheet"
                placeholder="720 222 222"
                minBottomsheetHeight={'50%'}
                initialBottomsheetHeight={'80%'}
                maxBottomsheetHeight={'92%'}
                popularCountries={PREFERRED_COUNTRIES}
                modalPopularCountriesTitle="Popular countries"
                modalAllCountriesTitle="All countries"
                onFocus={() => setWhatsappFocused(true)}
                onBlur={() => setWhatsappFocused(false)}
                onChangePhoneNumber={v => {
                  const startsWithZero = v.startsWith('0');
                  if (startsWithZero) {
                    v = v.slice(1);
                  }
                  setWhatsappInputValue(v);
                  const full = buildE164FromInput(
                    v,
                    whatsappSelectedCountry ||
                      whatsappInputRef.current?.getCountry?.(),
                  );
                  setField('whatsapp', full);
                }}
                country={whatsappSelectedCountry}
                onChangeCountry={c => {
                  setWhatsappSelectedCountry(c);
                  const full = buildE164FromInput(whatsappInputValue, c);
                  setField('whatsapp', full);
                }}
                phoneInputStyles={{
                  container: {
                    backgroundColor: 'transparent',
                    height: '100%',
                    alignItems: 'center',
                  },
                  flagContainer: {
                    paddingHorizontal: s(8),
                  },
                  divider: {
                    width: borderWidth(Platform.OS === 'web' ? 0.5 : 1),
                  },
                  callingCode: {
                    fontFamily: GTFonts.regular,
                    fontSize: fs(16),
                    lineHeight: fs(18),
                    color: theme.colors.text,
                  },
                  input: {
                    fontFamily: GTFonts.regular,
                    fontSize: fs(16),
                    lineHeight: fs(18),
                    color: theme.colors.text,
                    ...(Platform.OS === 'web' && { outlineStyle: 'none' }),
                  } as any,
                }}
              />
            </View>
            {whatsappFocused && form.whatsapp && !validWhatsapp ? (
              <HelperText type="error" visible>
                Invalid phone number
              </HelperText>
            ) : null}
          </>
        ) : null}

        {isEdit ? (
          <>
            <Text style={styles.sectionTitle}>Notes</Text>
            <TextInput
              mode="outlined"
              label="Notes"
              value={form.notes}
              onChangeText={text => setField('notes', text)}
              multiline
              numberOfLines={3}
              placeholderTextColor={theme.colors.textSecondary}
              {...inputOutlineProps}
              {...webPlaceholderTheme}
              style={[styles.input, { height: v(100) }]}
            />
          </>
        ) : null}

        <View style={styles.accordionSection}>
          <TouchableOpacity
            onPress={() => setIsAddressExpanded(prev => !prev)}
            activeOpacity={0.7}
            accessibilityRole="button"
            accessibilityState={{ expanded: isAddressExpanded }}
            style={styles.sectionHeaderRow}>
            <Text style={styles.accordionTitle}>Address & Tax</Text>
            <Ionicons
              name={isAddressExpanded ? 'chevron-up' : 'chevron-down'}
              size={iconSize(16)}
              color={theme.colors.text}
            />
          </TouchableOpacity>
          {isAddressExpanded ? (
            <View style={styles.accordionContent}>
              <TextInput
                mode="outlined"
                label="Physical Address"
                value={form.physicalAddress}
                onChangeText={text => setField('physicalAddress', text)}
                placeholderTextColor={theme.colors.textSecondary}
                {...inputOutlineProps}
                {...webPlaceholderTheme}
                style={styles.input}
              />
              <TextInput
                mode="outlined"
                label="Tax / VAT Number"
                value={form.taxVat}
                onChangeText={text => setField('taxVat', text)}
                placeholderTextColor={theme.colors.textSecondary}
                {...inputOutlineProps}
                {...webPlaceholderTheme}
                style={styles.input}
              />
            </View>
          ) : null}
        </View>
      </KeyboardAwareFormScroll>

      <KeyboardStickyView offset={{ closed: 0, opened: v(30) }}>
        <View style={styles.stickyButtonContainer}>
          <View style={styles.actions}>
            <Button
              mode="outlined"
              onPress={() => navigation.goBack()}
              disabled={saving}
              style={styles.actionButton}
              contentStyle={[styles.actionButtonContent, { height: v(50) }]}
              labelStyle={styles.actionLabel}
              textColor={theme.colors.textSecondary}
              theme={{ colors: { outline: theme.colors.border } }}
              uppercase={false}>
              Cancel
            </Button>
            <Button
              mode="contained"
              onPress={handleSubmit}
              disabled={!canSubmit}
              loading={saving}
              style={styles.actionButton}
              labelStyle={styles.actionLabel}
              contentStyle={[styles.actionButtonContent, { height: v(50) }]}
              buttonColor={theme.colors.successDark}
              textColor={theme.colors.textInverse}
              uppercase={false}>
              {saving ? 'Saving...' : isEdit ? 'Save Supplier' : 'Add Supplier'}
            </Button>
          </View>
        </View>
      </KeyboardStickyView>
    </View>
  );
};

const createStyles = (theme: any) =>
  StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: theme.colors.background,
    },
    sheetContent: {
      flex: 1,
      backgroundColor: theme.colors.surface,
      padding: s(10),
      ...(Platform.OS === 'web' && {
        maxWidth: WEB_MAX_CONTENT_WIDTH,
        alignSelf: 'center',
        width: '100%',
        padding: s(24),
      }),
    },
    sheetContentContainer: {
      flexGrow: 1,
      paddingBottom: v(80),
      gap: v(10),
      ...(Platform.OS === 'web' && {
        gap: v(20),
      }),
    },
    subtitle: {
      fontFamily: GTFonts.regular,
      fontSize: fs(13),
      lineHeight: fs(18),
      color: theme.colors.textSecondary,
      marginBottom: v(4),
    },
    sectionTitle: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(16),
      lineHeight: fs(18),
      color: theme.colors.textDark,
      marginVertical: v(6),
      ...(Platform.OS === 'web' && {
        fontSize: fs(18),
        // marginTop: v(24),
        marginBottom: v(12),
        paddingBottom: v(8),
        borderBottomWidth: 1,
        borderBottomColor: theme.colors.borderGray,
      }),
    },
    accordionSection: {
      marginTop: v(4),
    },
    sectionHeaderRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      ...(Platform.OS === 'web' && {
        paddingBottom: v(8),
        borderBottomWidth: 1,
        borderBottomColor: theme.colors.borderGray,
      }),
    },
    accordionTitle: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(16),
      lineHeight: fs(18),
      color: theme.colors.textDark,
      ...(Platform.OS === 'web' && {
        fontSize: fs(18),
      }),
    },
    accordionContent: {
      paddingTop: v(16),
      gap: v(10),
      ...(Platform.OS === 'web' && {
        gap: v(20),
      }),
    },
    fieldLabel: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(14),
      lineHeight: fs(16),
      color: theme.colors.textDark,
      marginBottom: v(2),
    },
    phoneInputOutline: {
      backgroundColor: theme.colors.surface,
      height: v(50),
      marginBottom: v(6),
      borderRadius: theme.borderRadius.md as number,
      borderWidth: StyleSheet.hairlineWidth,
      borderColor: theme.colors.borderGray,
      flexDirection: 'row',
      alignItems: 'center',
      ...(Platform.OS === 'web' && {
        height: v(56),
      }),
    },
    input: {
      backgroundColor: theme.colors.surface,
      height: v(50),
      marginBottom: v(6),
      borderRadius: theme.borderRadius.md,
      ...(Platform.OS === 'web' && {
        height: v(56),
      }),
    },
    pickerContainer: {
      backgroundColor: theme.colors.surface,
      borderWidth: 1,
      borderColor: theme.colors.borderGray,
      borderRadius: theme.borderRadius.md,
      // marginVertical: v(10),
      height: v(50),
      justifyContent: 'center',
      overflow: 'hidden',
      ...(Platform.OS === 'web' && {
        height: v(56),
      }),
    },
    picker: {
      height: v(60),
      color: theme.colors.text,
      ...(Platform.OS === 'web' && {
        height: v(56),
        borderWidth: 0,
        backgroundColor: 'transparent',
        outlineStyle: 'none',
        outlineWidth: 0,
        outlineColor: 'transparent',
        paddingHorizontal: 12,
      }),
    } as any,
    stickyButtonContainer: {
      backgroundColor: theme.colors.surface,
      paddingHorizontal: s(8),
      paddingVertical: s(12),
      borderTopWidth: borderWidth(Platform.OS === 'web' ? 0.5 : 1),
      borderTopColor: theme.colors.border,
      ...(Platform.OS === 'web' && {
        maxWidth: WEB_MAX_CONTENT_WIDTH,
        alignSelf: 'center',
        width: '100%',
        paddingHorizontal: s(24),
        borderTopWidth: 0,
      }),
    },
    actions: {
      flexDirection: 'row',
      gap: s(8),
    },
    actionButton: {
      flex: 1,
      borderRadius: theme.borderRadius.md,
    },
    actionButtonContent: {
      justifyContent: 'center',
      alignItems: 'center',
    },
    actionLabel: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(14),
      lineHeight: fs(18),
      textAlign: 'center',
    },
    loaderWrap: {
      justifyContent: 'center',
      alignItems: 'center',
      padding: s(24),
    },
    loaderText: {
      fontFamily: GTFonts.regular,
      fontSize: fs(14),
      color: theme.colors.textSecondary,
      marginTop: v(12),
    },
    errorText: {
      fontFamily: GTFonts.regular,
      fontSize: fs(14),
      color: theme.colors.errorDark,
      textAlign: 'center',
    },
  });

export default BackOfficeAddSupplierScreen;
